/**
 * OutlineReviewContent Component
 *
 * Drawer-compatible version of outline review UI.
 * Can be used within drawers or dialogs.
 *
 * Story: UX-003, Task 5
 */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { apiService } from '@/services/api'
import ReactMarkdown from 'react-markdown'

interface BlogPost {
  id: string
  title: string
  ai_metadata?: {
    phase: string
    outline?: string
    outline_revision_count?: number
  }
}

interface OutlineReviewContentProps {
  post: BlogPost
  onApprove?: () => void
  onRevise?: () => void
}

export function OutlineReviewContent({ post, onApprove, onRevise }: OutlineReviewContentProps) {
  const queryClient = useQueryClient()
  const [revisionFeedback, setRevisionFeedback] = useState('')

  const outline = post?.ai_metadata?.outline || ''
  const revisionCount = post?.ai_metadata?.outline_revision_count || 0

  // Approve outline mutation
  const approveMutation = useMutation({
    mutationFn: async () => {
      if (!post) throw new Error('No post selected')
      return apiService.blog.aiWriter.approveOutline(post.id)
    },
    onSuccess: () => {
      toast.success('Outline approved! AI is writing content now.')
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] })
      // Invalidate the specific post query to restart polling (since approval phase stops polling)
      queryClient.invalidateQueries({ queryKey: ['blog-post', post.id] })
      if (onApprove) onApprove()
    },
    onError: (error: any) => {
      console.error('Failed to approve outline:', error)
      toast.error(error?.response?.data?.detail || 'Failed to approve outline')
    },
  })

  // Revise outline mutation
  const reviseMutation = useMutation({
    mutationFn: async () => {
      if (!post || !revisionFeedback.trim()) throw new Error('No feedback provided')
      return apiService.blog.aiWriter.reviseOutline(post.id, {
        feedback: revisionFeedback,
      })
    },
    onSuccess: () => {
      toast.success('Regenerating outline with your feedback...')
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] })
      queryClient.invalidateQueries({ queryKey: ['blog-post', post.id] })
      setRevisionFeedback('')
      if (onRevise) onRevise()
    },
    onError: (error: any) => {
      console.error('Failed to request revisions:', error)
      toast.error(error?.response?.data?.detail || 'Failed to request revisions')
    },
  })

  const handleApprove = () => {
    approveMutation.mutate()
  }

  const handleRevise = () => {
    if (!revisionFeedback.trim()) {
      toast.error('Please provide feedback for the revision')
      return
    }
    reviseMutation.mutate()
  }

  // Show loading state while mutations are in progress
  if (approveMutation.isPending || reviseMutation.isPending) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-8 bg-card text-card-foreground">
        <div className="text-6xl mb-4 animate-pulse">🖊️</div>
        <h3 className="text-2xl font-semibold text-foreground mb-2">
          {approveMutation.isPending ? 'Writing your article...' : 'Regenerating outline...'}
        </h3>
        <p className="text-muted-foreground text-center">
          {approveMutation.isPending
            ? 'Your outline has been approved. The AI is now writing your full article.'
            : 'Creating a new outline with your feedback.'}
        </p>
      </div>
    )
  }

  if (!outline) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-8 bg-card text-card-foreground">
        <div className="text-6xl mb-4">⏳</div>
        <h3 className="text-2xl font-semibold text-foreground mb-2">Creating Outline...</h3>
        <p className="text-muted-foreground text-center">
          The AI is creating a detailed outline for your blog post.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-card text-card-foreground">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <h2 className="text-2xl font-semibold text-foreground">Review AI-Generated Outline</h2>
        <p className="text-muted-foreground mt-1">
          Review the outline below and approve it to continue, or request revisions.
          {revisionCount > 0 && (
            <span className="block mt-1 text-sm">
              (Revision #{revisionCount + 1})
            </span>
          )}
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Outline Preview */}
        <div className="space-y-2">
          <Label className="text-base font-semibold text-foreground">
            Outline Preview
          </Label>
          <div className="rounded-lg border border-border bg-background p-6 prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown>{outline}</ReactMarkdown>
          </div>
        </div>

        {/* Revision Feedback Section */}
        <div className="space-y-2">
          <Label htmlFor="revision-feedback" className="text-base font-semibold text-foreground">
            Request Changes (optional)
          </Label>
          <Textarea
            id="revision-feedback"
            value={revisionFeedback}
            onChange={(e) => setRevisionFeedback(e.target.value)}
            placeholder="E.g., Add more details about X, include a section on Y, expand the introduction..."
            rows={4}
            className="bg-background border-border text-foreground placeholder:text-muted-foreground focus:ring-ring"
          />
          {revisionFeedback.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {revisionFeedback.length} characters
            </p>
          )}
        </div>
      </div>

      {/* Footer with actions */}
      <div className="p-6 border-t border-border bg-muted/5">
        <div className="flex gap-2 justify-between">
          <Button
            variant="outline"
            onClick={handleRevise}
            disabled={!revisionFeedback.trim() || reviseMutation.isPending}
            className="border-border hover:bg-accent"
          >
            {reviseMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Requesting Revisions...
              </>
            ) : (
              'Request Revisions'
            )}
          </Button>

          <Button
            onClick={handleApprove}
            disabled={approveMutation.isPending}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {approveMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Approving...
              </>
            ) : (
              'Approve & Continue'
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
